import "@/styles/SimpleKeyboardOverrides.css";
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Charge } from "@/pages/sy/models/Charge";
import { getNextChargeNumberByHeatName } from "@/pages/sy/repositories/ChargeRepository";
import {
  useOpenChargeMutation,
  useUpsertChargeMutation,
} from "@/pages/sy/hooks/queries/useChargeMutations";
import { useBucketsQuery } from "@/pages/sy/hooks/queries/useBucketsQuery";
import { useRecipesQuery } from "@/pages/sy/hooks/queries/useRecipesQuery";
import { newChargePanelStyles } from "@/pages/sy/styles/NewChargePanelStyles";
import { CustomKeypad } from "@/pages/sy/components/CustomKeypad";
import { ErrorDetailsModal } from "../modals/ErrorDetailsModal";

interface NewChargePanelProps {
  open: boolean;
  onSchedule: () => void;
  onCancel: () => void;
  onStart?: () => void;
  operatorId: number | null;
  chargeGroupNumber?: number | null;
  scheduledHeatName?: string | null;
  currentHeatName?: string | null;
  presetChargeNumber?: number;
  disableHeatNameEdit?: boolean;
  charges?: Charge[];
}

export const NewChargePanel = ({
  open,
  onSchedule,
  onCancel,
  onStart,
  operatorId,
  chargeGroupNumber,
  scheduledHeatName,
  currentHeatName,
  presetChargeNumber,
  disableHeatNameEdit = false,
  charges = [],
}: NewChargePanelProps) => {
  const theme = useTheme();
  const styles = useMemo(() => newChargePanelStyles(theme), [theme]);
  const navigate = useNavigate();

  const { data: recipes = [], isLoading: recipesLoading } = useRecipesQuery(
    true,
    true,
  );
  const { data: buckets = [], isLoading: bucketsLoading } = useBucketsQuery();
  const upsertChargeMutation = useUpsertChargeMutation();
  const openChargeMutation = useOpenChargeMutation();
  const staticDataLoading = recipesLoading || bucketsLoading;
  const isLoading =
    upsertChargeMutation.isPending || openChargeMutation.isPending;

  const [selectedBucket, setSelectedBucket] = useState<number | null>(null);
  const [currentChargeNumber, setCurrentChargeNumber] = useState<number | null>(
    null,
  );
  const [firstLetter, setFirstLetter] = useState<string>("");
  const [secondLetter, setSecondLetter] = useState<string>("");
  const [heatNumber, setHeatNumber] = useState<string>("");
  const [selectedRecipeId, setSelectedRecipeId] = useState<number | null>(null);
  const [error, setError] = useState("");

  const heatInputRef = useRef<HTMLInputElement>(null);

  // Helper to get full heat name from components
  const getFullHeatName = () => {
    return `${firstLetter}${secondLetter}${heatNumber}`;
  };

  useEffect(() => {
    if (presetChargeNumber) {
      setCurrentChargeNumber(presetChargeNumber);
    }
  }, [presetChargeNumber]);

  useEffect(() => {
    if (scheduledHeatName) {
      // Parse scheduledHeatName into components (e.g., "AB1234" -> "A", "B", "1234")
      const match = scheduledHeatName.match(/^([A-Z])([A-Z])(\d{4})$/i);
      if (match) {
        setFirstLetter(match[1].toUpperCase());
        setSecondLetter(match[2].toUpperCase());
        setHeatNumber(match[3]);
      }
      // Automatically check for existing charges when heat name is provided
      handleHeatCheck(scheduledHeatName);
    }
  }, [scheduledHeatName]);

  const handleHeatCheck = async (heatName: string) => {
    // Clear any previous errors
    setError("");

    if (!heatName.trim()) {
      setCurrentChargeNumber(null);
      setSelectedRecipeId(null);
      return;
    }

    // Validate heat name format: 2 letters + 4 digits (e.g., AB1234)
    const heatNamePattern = /^[A-Z]{2}\d{4}$/;
    if (!heatNamePattern.test(heatName.toUpperCase())) {
      setError(
        "Heat name must be 2 letters followed by 4 digits (e.g., AB1234)",
      );
      setCurrentChargeNumber(null);
      setSelectedRecipeId(null);
      return;
    }

    try {
      // Call backend to get next charge number
      const result = await getNextChargeNumberByHeatName(heatName);

      if (result.ok) {
        const nextChargeNumber =
          result.data?.tables?.[0]?.rows?.[0]?.NextChargeNumber;
        setCurrentChargeNumber(nextChargeNumber || 1);

        // Get recipe from existing charges (backend will validate)
        const heatCharges = charges.filter(
          (c: Charge) =>
            c.ScheduledHeatName?.toUpperCase() === heatName.toUpperCase(),
        );
        const chargeWithRecipe = heatCharges.find((c: Charge) => c.RecipeId);
        setSelectedRecipeId(chargeWithRecipe?.RecipeId || null);
      } else {
        // Fallback to 1 if API call fails
        setCurrentChargeNumber(1);
        setSelectedRecipeId(null);
        setError(result.message || "Failed to get next charge number");
      }
    } catch (error) {
      console.error("Failed to get next charge number:", error);
      // Fallback to 1 if error occurs
      setCurrentChargeNumber(1);
      setSelectedRecipeId(null);
      setError(
        "Error getting next charge number: " +
          (error instanceof Error ? error.message : String(error)),
      );
    }
  };

  const handleHeatNumberChange = (input: string) => {
    // Only allow digits and limit to 4 characters
    const digitsOnly = input.replace(/\D/g, "").slice(0, 4);
    setHeatNumber(digitsOnly);

    // Trigger heat check when 4 digits are entered and letters are selected
    if (digitsOnly.length === 4 && firstLetter && secondLetter) {
      const fullHeatName = `${firstLetter}${secondLetter}${digitsOnly}`;
      handleHeatCheck(fullHeatName);
    }
  };

  const handleKeyboardKeyPress = (button: string) => {
    if (button === "{clear}") {
      setHeatNumber("");
    }
  };

  const handleHeatNumberBlur = () => {
    // Trigger heat check when number input loses focus
    if (firstLetter && secondLetter && heatNumber.length === 4) {
      handleHeatCheck(getFullHeatName());
    }
  };

  const handleResetToCurrentHeat = () => {
    if (currentHeatName) {
      // Parse currentHeatName into components
      const match = currentHeatName.match(/^([A-Z])([A-Z])(\d{4})$/i);
      if (match) {
        setFirstLetter(match[1].toUpperCase());
        setSecondLetter(match[2].toUpperCase());
        setHeatNumber(match[3]);
      }
      handleHeatCheck(currentHeatName);
    }
  };

  const handleBucketButtonClick = (bucketId: number) => {
    setSelectedBucket(bucketId);
  };

  // Check if the selected bucket is already used in an open or close charge
  const isBucketUsedInOpenCharge =
    selectedBucket !== null &&
    charges.some(
      (charge) =>
        charge.BucketId === selectedBucket &&
        (charge.ChargeStatusCode === "O" || charge.ChargeStatusCode === "C"),
    );

  const handleSchedule = () => {
    if (!selectedBucket) {
      setError("Please select a bucket");
      return;
    }
    const fullHeatName = getFullHeatName();
    if (!fullHeatName.trim() || fullHeatName.length !== 6) {
      setError("Please enter a complete heat name (2 letters + 4 digits)");
      return;
    }
    // Recipe is required only for charge #1 in Selection Screen
    if (
      !disableHeatNameEdit &&
      currentChargeNumber === 1 &&
      !selectedRecipeId
    ) {
      setError("Please select a recipe");
      return;
    }

    // Recipe is null when heat name edit is disabled OR charge > 3
    const recipeToUse =
      disableHeatNameEdit ||
      (currentChargeNumber !== null && currentChargeNumber > 3)
        ? null
        : selectedRecipeId;

    const newCharge: Partial<Charge> = {
      OperatorId: operatorId,
      ChargeStatusCode: "S",
      ChargeNumber: currentChargeNumber,
      BucketId: selectedBucket,
      RecipeId: recipeToUse,
      ScheduledHeatName: fullHeatName.toUpperCase(),
    };

    // Add ChargeGroupNumber if provided
    if (chargeGroupNumber !== undefined && chargeGroupNumber !== null) {
      newCharge.ChargeGroupNumber = chargeGroupNumber;
    }

    upsertChargeMutation.mutate(newCharge as Charge, {
      onSuccess: () => {
        onSchedule();
      },
      onError: (error) => {
        console.error("Error creating charge:", error);
        setError(
          error instanceof Error
            ? error.message
            : "Error creating charge: " + error,
        );
      },
    });
  };

  const handleStart = async () => {
    if (!selectedBucket) {
      setError("Please select a bucket");
      return;
    }
    if (isBucketUsedInOpenCharge) {
      setError("The selected bucket is already in use by an open charge");
      return;
    }
    const fullHeatName = getFullHeatName();
    if (!fullHeatName.trim() || fullHeatName.length !== 6) {
      setError("Please enter a complete heat name (2 letters + 4 digits)");
      return;
    }
    // Recipe is required only for charge #1 in Selection Screen
    if (
      !disableHeatNameEdit &&
      currentChargeNumber === 1 &&
      !selectedRecipeId
    ) {
      setError("Please select a recipe");
      return;
    }
    try {
      // Recipe is null when heat name edit is disabled OR charge > 3
      const recipeToUse =
        disableHeatNameEdit ||
        (currentChargeNumber !== null && currentChargeNumber > 3)
          ? null
          : selectedRecipeId;

      const newCharge: Partial<Charge> = {
        OperatorId: operatorId,
        ChargeStatusCode: "S",
        BucketId: selectedBucket,
        ChargeNumber: currentChargeNumber,
        RecipeId: recipeToUse,
        ScheduledHeatName: fullHeatName.toUpperCase(),
      };

      // Add ChargeGroupNumber if provided
      if (chargeGroupNumber !== undefined && chargeGroupNumber !== null) {
        newCharge.ChargeGroupNumber = chargeGroupNumber;
      }

      const result = await upsertChargeMutation.mutateAsync(
        newCharge as Charge,
      );
      const resp = result.data?.tables?.[0]?.rows?.[0];

      await openChargeMutation.mutateAsync(resp.ChargeId);

      // Call onStart callback if provided (to close modal in parent)
      if (onStart) {
        onStart();
      }

      navigate(
        `/scrap-charges/operation/${resp.ChargeId}/start/${
          resp.ChargeGroupNumber
        }?operatorId=${operatorId || ""}`,
      );
    } catch (err) {
      console.error("Error creating charge:", err);
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const enabledRecipes = recipes.filter((r) => r.IsEnable);

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <Typography variant="h5" sx={styles.title}>
        NEW CHARGE {currentChargeNumber ? `#${currentChargeNumber}` : ""}
      </Typography>

      <Grid container spacing={1} sx={{ mt: 1, mb: 3 }}>
        {/* Left side: Form fields */}
        <Grid item xs={12} md={disableHeatNameEdit ? 12 : 6}>
          <Grid container spacing={4}>
            {/* Row 1: Heat Input (Letter Selectors + Number Input) and Charge # */}
            <Grid item xs={12}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Heat
                </Typography>
                {disableHeatNameEdit ? (
                  <Box sx={{ width: "90%" }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={getFullHeatName()}
                      disabled
                      placeholder="Heat name"
                      sx={styles.heatInput}
                      slotProps={{
                        input: {
                          sx: {
                            fontSize: "1.5rem",
                            fontWeight: 600,
                            height: "70px",
                          },
                        },
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ width: "90%" }}>
                    <Grid container spacing={2} sx={{ alignItems: "center" }}>
                      {/* First Letter Selector */}
                      <Grid item sx={{ flex: "0 0 15%" }}>
                        <FormControl variant="outlined" fullWidth>
                          <Select
                            value={firstLetter}
                            onChange={(e: SelectChangeEvent<string>) =>
                              setFirstLetter(e.target.value)
                            }
                            displayEmpty
                            renderValue={(value: string) => {
                              if (!value || value === "") {
                                return (
                                  <Typography
                                    component="span"
                                    sx={{
                                      color: (theme) =>
                                        theme.palette.text.disabled,
                                    }}
                                  >
                                    -
                                  </Typography>
                                );
                              }
                              return value;
                            }}
                            sx={{
                              fontSize: "1.5rem",
                              fontWeight: 600,
                              height: "70px",
                              "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                                height: "70px",
                                padding: "0 14px",
                              },
                            }}
                          >
                            <MenuItem value="" disabled>
                              <Typography
                                component="span"
                                sx={{
                                  color: (theme) => theme.palette.text.disabled,
                                }}
                              >
                                Select
                              </Typography>
                            </MenuItem>
                            {Array.from({ length: 26 }, (_, i) =>
                              String.fromCharCode(65 + i),
                            ).map((letter) => (
                              <MenuItem
                                key={letter}
                                value={letter}
                                sx={{
                                  fontSize: "1.5rem",
                                  fontWeight: 600,
                                  padding: "16px 24px",
                                }}
                              >
                                {letter}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {/* Second Letter Selector */}
                      <Grid item sx={{ flex: "0 0 15%" }}>
                        <FormControl variant="outlined" fullWidth>
                          <Select
                            value={secondLetter}
                            onChange={(e: SelectChangeEvent<string>) =>
                              setSecondLetter(e.target.value)
                            }
                            displayEmpty
                            renderValue={(value: string) => {
                              if (!value || value === "") {
                                return (
                                  <Typography
                                    component="span"
                                    sx={{
                                      color: (theme) =>
                                        theme.palette.text.disabled,
                                    }}
                                  >
                                    -
                                  </Typography>
                                );
                              }
                              return value;
                            }}
                            sx={{
                              fontSize: "1.5rem",
                              fontWeight: 600,
                              height: "70px",
                              "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                                height: "70px",
                                padding: "0 14px",
                              },
                            }}
                          >
                            <MenuItem value="" disabled>
                              <Typography
                                component="span"
                                sx={{
                                  color: (theme) => theme.palette.text.disabled,
                                }}
                              >
                                Select
                              </Typography>
                            </MenuItem>
                            {Array.from({ length: 26 }, (_, i) =>
                              String.fromCharCode(65 + i),
                            ).map((letter) => (
                              <MenuItem
                                key={letter}
                                value={letter}
                                sx={{
                                  fontSize: "1.5rem",
                                  fontWeight: 600,
                                  padding: "16px 24px",
                                }}
                              >
                                {letter}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {/* Number Input */}
                      <Grid item sx={{ flex: "0 0 25%" }}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={heatNumber}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleHeatNumberChange(e.target.value)
                          }
                          onBlur={handleHeatNumberBlur}
                          placeholder="0000"
                          inputRef={heatInputRef}
                          autoFocus
                          sx={styles.heatInput}
                          slotProps={{
                            input: {
                              sx: {
                                fontSize: "1.5rem",
                                fontWeight: 600,
                                height: "70px",
                                "& input": {
                                  textAlign: "center",
                                },
                              },
                            },
                            htmlInput: {
                              maxLength: 4,
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                            },
                          }}
                        />
                      </Grid>
                      {/* USE EAF HEAT Button */}
                      {currentHeatName ? (
                        <Grid item xs>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleResetToCurrentHeat}
                            fullWidth
                            sx={{
                              height: "70px",
                              width: "70%",
                              fontSize: "1rem",
                              fontWeight: 600,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            }}
                          >
                            EAF HEAT
                          </Button>
                        </Grid>
                      ) : null}
                    </Grid>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Row 2: Recipe Selector - Hidden when heat name edit is disabled or charge > 3 */}
            {!disableHeatNameEdit &&
            currentChargeNumber !== null &&
            currentChargeNumber <= 3 ? (
              <Grid item xs={12}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Recipe
                  </Typography>
                  <Box sx={{ width: "80%" }}>
                    <FormControl variant="outlined" fullWidth>
                      <Select
                        value={selectedRecipeId ?? ""}
                        onChange={(e: SelectChangeEvent<string | number>) => {
                          const val = e.target.value;
                          setSelectedRecipeId(val === "" ? null : Number(val));
                        }}
                        disabled={
                          currentChargeNumber !== null &&
                          currentChargeNumber > 1
                        }
                        displayEmpty
                        renderValue={(value: string | number) => {
                          if (!value || value === "") {
                            return (
                              <Typography
                                component="span"
                                sx={{
                                  color: (theme) => theme.palette.text.disabled,
                                }}
                              >
                                Select a recipe
                              </Typography>
                            );
                          }
                          const selectedRecipe = enabledRecipes.find(
                            (r) => r.RecipeId === value,
                          );
                          return selectedRecipe?.RecipeName || "";
                        }}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: "center",
                            horizontal: "center",
                          },
                          transformOrigin: {
                            vertical: "center",
                            horizontal: "center",
                          },
                          disablePortal: false,
                          slotProps: {
                            paper: {
                              sx: {
                                maxHeight: 700,
                                zIndex: 10001,
                              },
                            },
                          },
                          MenuListProps: {
                            sx: {
                              padding: "8px 0",
                            },
                          },
                        }}
                        sx={{
                          fontSize: "1.5rem",
                          fontWeight: 600,
                          height: "70px",
                        }}
                      >
                        {enabledRecipes.map((recipe) => (
                          <MenuItem
                            key={recipe.RecipeId ?? undefined}
                            value={recipe.RecipeId ?? undefined}
                            sx={{
                              fontSize: "2rem",
                              fontWeight: 600,
                              padding: "20px 24px",
                              minHeight: "70px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {recipe.RecipeName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Grid>
            ) : null}

            {/* Row 3: Bucket Buttons */}
            <Grid item xs={12}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Bucket
                </Typography>
                <Box sx={{ width: "90%" }}>
                  <Grid container spacing={4}>
                    {buckets.map((bucket) => (
                      <Grid item key={bucket.BucketId}>
                        <Button
                          variant={
                            selectedBucket === bucket.BucketId
                              ? "contained"
                              : "outlined"
                          }
                          color="primary"
                          onClick={() =>
                            handleBucketButtonClick(bucket.BucketId)
                          }
                          sx={styles.bucketButton}
                        >
                          {bucket.BucketName}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        {/* Right side: Numeric Keypad */}
        {!disableHeatNameEdit ? (
          <Grid item xs={12} md={6}>
            <Box>
              <CustomKeypad
                value={heatNumber}
                onChange={(input: string) => handleHeatNumberChange(input)}
                onKeyPress={handleKeyboardKeyPress}
                layout={{
                  default: ["7 8 9", "4 5 6", "1 2 3", "{bksp} 0 {clear}"],
                }}
                display={{
                  "{bksp}": "⌫",
                  "{clear}": "C",
                }}
              />
            </Box>
          </Grid>
        ) : null}
      </Grid>

      <Box sx={styles.buttonsContainer}>
        <Button onClick={onCancel} sx={styles.cancelButton}>
          Cancel
        </Button>
        <Button
          disabled={
            selectedBucket === null ||
            !firstLetter ||
            !secondLetter ||
            heatNumber.length !== 4 ||
            currentChargeNumber === null ||
            (!disableHeatNameEdit &&
              currentChargeNumber === 1 &&
              !selectedRecipeId) ||
            isLoading ||
            staticDataLoading
          }
          onClick={handleSchedule}
          sx={styles.scheduleButton}
        >
          Schedule
        </Button>
        <Button
          disabled={
            selectedBucket === null ||
            !firstLetter ||
            !secondLetter ||
            heatNumber.length !== 4 ||
            currentChargeNumber === null ||
            (!disableHeatNameEdit &&
              currentChargeNumber === 1 &&
              !selectedRecipeId) ||
            isLoading ||
            staticDataLoading ||
            isBucketUsedInOpenCharge
          }
          onClick={handleStart}
          sx={styles.startButton}
        >
          Start
        </Button>
      </Box>

      <ErrorDetailsModal
        open={!!error}
        title="Validation Error"
        message={error}
        onClose={() => setError("")}
      />
    </Box>
  );
};
